/**
 * use-job-types — tenant-owned, VERSION-AWARE job-type catalog (DEC-140, Phase 09.1).
 *
 * GET /v1/job-types (CREATE_JOB-gated) returns THIS tenant's LIVE job types. The
 * New Job form reads id/code/label (optionally customer-scoped, JOBTYPE-13); the
 * setup-wizard editor reads the full config + writes via create/edit
 * (MANAGE_REFERENCE_DATA). Active-only by default; `?includeInactive=1` for the
 * editor.
 *
 * THIS IS THE SINGLE SOURCE OF TRUTH for the job-type shape and its enum
 * constants. Every web-dispatch surface (NewJobModal, JobTypesEditor, the
 * create-job wizard) must import `JobType` / `RequirementLevel` /
 * `TicketShape` / `TankCountMode` / `STRUCTURAL_FIELDS` from HERE rather than
 * re-declaring them — four independent declarations of the same concept is
 * exactly how the enum widening became a four-package breaking change
 * (09.1-RESEARCH Pitfall 3).
 */

import { useApiQuery, useApiOpts } from '@lodex-pro/web-shared';

/** JOBTYPE-05/06/07 — the Hidden/Optional/Required three-state. */
export const REQUIREMENT_LEVELS = ['HIDDEN', 'OPTIONAL', 'REQUIRED'] as const;
export type RequirementLevel = (typeof REQUIREMENT_LEVELS)[number];

/** D-03 / D-13 — the fork. LINE_ITEM is selectable; its CAPTURE path is NOT in 09.1. */
export const TICKET_SHAPES = ['TANK', 'LINE_ITEM'] as const;
export type TicketShape = (typeof TICKET_SHAPES)[number];

export const TANK_COUNT_MODES = ['SINGLE', 'MAX_N', 'UNLIMITED'] as const;
export type TankCountMode = (typeof TANK_COUNT_MODES)[number];

/** Human labels for TANK_COUNT_MODES. Single source — both JobTypesEditor's
 *  flat edit form and JobTypeWizard's step 5 (Plan 09.1-11) import this
 *  rather than re-declaring it (avoids a JobTypesEditor <-> JobTypeWizard
 *  circular import: JobTypesEditor renders JobTypeWizard, so JobTypeWizard
 *  must not import anything back from JobTypesEditor.tsx). */
export const TANK_COUNT_MODE_LABELS: Record<TankCountMode, string> = {
  SINGLE: 'Single',
  MAX_N: 'Max N',
  UNLIMITED: 'Unlimited',
};

/** billingMethod / captureMethod / completionLocation allowlists. Single
 *  source for the same circular-import reason as TANK_COUNT_MODE_LABELS
 *  above — both JobTypesEditor.tsx and JobTypeWizard.tsx import these. */
// Phase 12.1 — widened to include PER_UNIT (D-06). Mirrors the API Zod gate in
// packages/api/src/routes/job-types/list.ts and migration 0075's DB CHECK; must
// stay in lockstep with both. This constant drives BOTH JobTypeWizard.tsx and
// JobTypesEditor.tsx's billing-method <select>.
export const BILLING_METHODS = ['PER_BBL', 'HOURLY', 'PER_LOAD', 'PER_UNIT'] as const;
export const CAPTURE_METHODS = ['SINGLE_GAUGE', 'BEFORE_AFTER'] as const;
export const COMPLETION_LOCATIONS = ['DISPOSAL_FACILITY', 'ON_SITE', 'OTHER'] as const;

/** D-01/D-04 — editing ANY of these forks a new version row. Mirrors the
 *  server's STRUCTURAL_FIELDS allowlist in packages/api/src/routes/job-types/list.ts. */
export const STRUCTURAL_FIELDS = [
  'ticketShape',
  'tankCountMode',
  'tankCountMax',
  // DEC-176 — default product + unit are versioned; changing either forks (server matches).
  'defaultProductId',
  'unitOfMeasureCode',
] as const;

/** Human labels for the three-state control. Single source for every surface. */
export const REQUIREMENT_LABELS: Record<RequirementLevel, string> = {
  HIDDEN: 'Hidden',
  OPTIONAL: 'Optional',
  REQUIRED: 'Required',
};

/** OPEN-121 — the help text under the job type's Photo select (editor and wizard). */
export const PHOTO_REQUIREMENT_HELP =
  'Required: the driver takes a load photo or gives a reason for skipping it, and the ticket is flagged for review.';

/** A tenant's job-type row from GET /v1/job-types (full versioned projection).
 *  Field names match the API's wire response EXACTLY
 *  (packages/api/src/routes/job-types/list.ts SELECT_COLS / JobTypeRow). */
export interface JobType {
  readonly id: number;
  readonly code: string;
  readonly label: string;
  readonly billingMethod: string;
  readonly defaultRateCents: number | null;
  readonly unitOfMeasureCode: string | null;
  // DEC-176 — job-type default product (drives the unit; structural/versioned) +
  // the per-tank require-product rule (OPTIONAL/REQUIRED; non-structural).
  readonly defaultProductId: string | null;
  readonly productRequirement: 'OPTIONAL' | 'REQUIRED' | null;
  readonly captureType: string;
  readonly captureMethod: string;
  readonly completionLocation: string;
  // DEPRECATED (JOBTYPE-05) — server mirror of the *_requirement enums. Read
  // the ENUM, never these. Dropped in a follow-up migration outside 09.1.
  readonly sealRequired: boolean;
  readonly requireWaterVolume: boolean;
  readonly requireOilVolume: boolean;
  readonly requireGaugeReadings: boolean;
  readonly requireSignatureReason: boolean;
  readonly isDefaultForDispatch: boolean;
  readonly isActive: boolean;
  readonly familyId: string;
  readonly version: number;
  readonly supersededAt: string | null;
  readonly ticketShape: TicketShape;
  readonly tankCountMode: TankCountMode | null;
  readonly tankCountMax: number | null;
  readonly waterVolumeRequirement: RequirementLevel | null;
  readonly oilVolumeRequirement: RequirementLevel | null;
  readonly topGaugeRequirement: RequirementLevel | null;
  readonly bottomGaugeRequirement: RequirementLevel | null;
  readonly sealRequirement: RequirementLevel | null;
  readonly colorCutRequirement: RequirementLevel | null;
  readonly tankNameRequirement: RequirementLevel | null;
  readonly driverCommentRequirement: RequirementLevel;
  /** OPEN-121 — the load-photo rule. REQUIRED: a photo, or a reason for skipping it. */
  readonly photoRequirement: RequirementLevel;
  readonly driverTimeRequirement: RequirementLevel;
  readonly useCompanyPricing: boolean;
  readonly disposalFeeEnabled: boolean;
}

/** Response envelope for GET /v1/job-types. */
export interface JobTypesResponse {
  readonly jobTypes: readonly JobType[];
}

/** Preflight response for GET /v1/job-types/:id/edit-impact (JOBTYPE-12). */
export interface JobTypeEditImpact {
  readonly jobTypeId: number;
  readonly familyId: string;
  readonly version: number;
  readonly isSuperseded: boolean;
  readonly jobCount: number;
  readonly hasExistingTickets: boolean;
  readonly structuralFields: readonly string[];
}

/** Stable array query key (RESEARCH Pitfall 4 — avoid cache collisions). */
export const JOB_TYPES_QUERY_KEY = ['job-types', 'catalog'] as const;
/** Editor key — includes inactive rows so deactivated types stay visible. */
export const JOB_TYPES_ADMIN_QUERY_KEY = ['job-types', 'catalog', 'admin'] as const;

/** Fetch the ACTIVE job-type catalog (New Job picker). staleTime 5 min — near-static. */
export function useJobTypes(
  apiOpts: ReturnType<typeof useApiOpts>,
): ReturnType<typeof useApiQuery<JobTypesResponse>> {
  return useApiQuery<JobTypesResponse>(apiOpts, '/v1/job-types', {
    queryKey: JOB_TYPES_QUERY_KEY,
    staleTime: 300_000,
  });
}

/** Fetch ALL job types incl. inactive (setup-wizard editor). */
export function useJobTypesAdmin(
  apiOpts: ReturnType<typeof useApiOpts>,
): ReturnType<typeof useApiQuery<JobTypesResponse>> {
  return useApiQuery<JobTypesResponse>(apiOpts, '/v1/job-types?includeInactive=1', {
    queryKey: JOB_TYPES_ADMIN_QUERY_KEY,
    staleTime: 30_000,
  });
}

/** JOBTYPE-13/15 — the New Job picker, scoped to the selected customer.
 *  customerId === null → the unscoped list (identical to useJobTypes).
 *  The server's sentinel: a family with NO scoping rows is available to EVERY
 *  customer, so a scoped list is a SUPERSET-filtered view, not an opt-in list. */
export function useJobTypesForCustomer(
  apiOpts: ReturnType<typeof useApiOpts>,
  customerId: string | null,
): ReturnType<typeof useApiQuery<JobTypesResponse>> {
  const path =
    customerId === null
      ? '/v1/job-types'
      : `/v1/job-types?customerId=${encodeURIComponent(customerId)}`;
  return useApiQuery<JobTypesResponse>(apiOpts, path, {
    // DISTINCT key per customer so a customer switch does not serve a stale
    // scoped list from the unscoped (or a different customer's) cache
    // (T-096-02).
    queryKey: ['job-types', 'catalog', 'customer', customerId ?? 'all'] as const,
    staleTime: 300_000,
  });
}

/** JOBTYPE-12 — preflight for the edit form's "this will create a new version"
 *  warning (DEC-170 guardrail). Pass jobTypeId:null until the form actually needs it. */
export function useJobTypeEditImpact(
  apiOpts: ReturnType<typeof useApiOpts>,
  jobTypeId: number | null,
): ReturnType<typeof useApiQuery<JobTypeEditImpact>> {
  return useApiQuery<JobTypeEditImpact>(
    apiOpts,
    `/v1/job-types/${String(jobTypeId ?? '')}/edit-impact`,
    {
      queryKey: ['job-types', 'edit-impact', jobTypeId] as const,
      // This must never be served stale — a stale `hasExistingTickets: false`
      // is exactly the warning that must not be missed (T-096-03).
      staleTime: 0,
      enabled: jobTypeId !== null,
    },
  );
}

/** D-11 — what the driver is shown/required to type on a TANK-shaped ticket. */
export function isTankShape(jt: Pick<JobType, 'ticketShape'>): boolean {
  return jt.ticketShape === 'TANK';
}

/** Never confuse with the positional slot identifier
 *  (haul_capture_tanks.position, DEC-023/DEC-025) — this is about a FIELD's
 *  requirement level, not a tank's position in the ticket. */
export function isFieldVisible(level: RequirementLevel | null): boolean {
  return level !== null && level !== 'HIDDEN';
}

export function isFieldRequired(level: RequirementLevel | null): boolean {
  return level === 'REQUIRED';
}

/** Which STRUCTURAL_FIELDS a draft changes relative to the saved row. Empty
 *  array means the edit applies in place; non-empty means the server will
 *  fork a new version and the UI must warn first (DEC-170). Sending an
 *  unchanged value must NOT count as a change — otherwise every save from
 *  the edit form would fork a version. Mirrors the server's own
 *  present-in-body-AND-differs-from-current classification
 *  (packages/api/src/routes/job-types/list.ts PATCH handler). */
export function changedStructuralFields(
  saved: JobType,
  draft: Partial<JobType>,
): readonly string[] {
  return STRUCTURAL_FIELDS.filter((f) => f in draft && draft[f] !== saved[f]);
}
